<template>
  <avs-card>
    <template v-slot:title>
      <v-toolbar
        class="mt-4 custom-toolbar"
        elevation="0"
      >
        <v-text-field
          clearable
          hide-details
          prepend-inner-icon="mdi-magnify"
          :label="$t('commons.search')"
          v-model="search"
        />
        <slot
          name="actions"
          v-bind:dataset="dataset"
        />
      </v-toolbar>
    </template>
    <template v-slot:content>
      <v-data-table
        class="ma-10"
        :headers="headers"
        :no-data-text="$t('messages.information.no_data')"
        :search="search"
        :items="dataset"
        :loading-text="$t('categories.nonconformity.loading')"
        :no-results-text="$tc('categories.nonconformity.name', 0)"
        :loading="loading"
        disable-pagination
        hide-default-footer
        @dblclick:row="($event, { item }) => toEdit(item)"
      >
        <template #item.report_date="{ item }">
          {{ toDate(item.report_date) }}
        </template>
        <template v-slot:item.status="{ item }">
          <component :is="handleStatusComponent(item)" />
        </template>
        <template v-slot:item.quick_description="{ item }">
          {{ truncate(item.quick_description) }}
        </template>
        <template v-slot:item.pictures="{ item }">
          <v-chip>
            <v-icon left>mdi-image-multiple</v-icon>
            {{ item.pictures.length }} {{ $tc("commons.picture", 2) }}
          </v-chip>
        </template>
        <template v-slot:item.criticality="{ item }">
          <v-chip
            class="ma-2"
            label
            color="yellow"
            v-if="item.criticality == 'Minor'"
          >
            <v-icon left>mdi-exclamation</v-icon>
            {{ $t("commons.criticality_items.minor") }}
          </v-chip>
          <v-chip
            class="ma-2"
            label
            color="orange"
            v-else-if="item.criticality == 'Major'"
          >
            <v-icon left>mdi-exclamation-thick</v-icon>
            {{ $t("commons.criticality_items.major") }}
          </v-chip>
          <v-chip
            class="ma-2"
            label
            color="#f8c471"
            v-else-if="item.criticality == 'Signal'"
          >
            <v-icon left>mdi-cancel</v-icon>
            {{ $t("commons.criticality_items.signal") }}
          </v-chip>
          <v-chip
            class="ma-2"
            label
            color="red"
            v-else
          >
            <v-icon left>mdi-alert-outline</v-icon>
            {{ $t("commons.criticality_items.critical") }}
          </v-chip>
        </template>
        <template v-slot:item.total_actions="{ item }">
          <v-tooltip
            bottom
            v-if="item.total_actions > 0"
          >
            <template v-slot:activator="{ on, attrs }">
              <v-chip
                v-bind="attrs"
                v-on="on"
                class="ma-2"
                color="blue"
                outlined
              >
                <v-icon left>mdi-format-list-bulleted</v-icon>
                {{ item.total_actions }}
              </v-chip>
            </template>
            <span>
              <ul>
                <li
                  v-for="(action, index) in item.actions_list"
                  :key="index"
                >
                  {{ action }}
                </li>
              </ul>
            </span>
          </v-tooltip>
          <v-chip
            v-else
            class="ma-2"
            color="grey"
            outlined
          >
            <v-icon left>mdi-close-circle-outline</v-icon>
            0
          </v-chip>
        </template>

        <template v-slot:item.actions="{ item }">
          <div class="d-flex justify-end">
            <avs-editButton
              @click.stop="toEdit(item)"
              :spantext="
                $t('actions.edit', {
                  entity: $tc('categories.nonconformity.name', 1).toLowerCase()
                })
              "
            />
            <avs-button
              icon
              color="secondary"
              :iconI="'mdi-download-outline'"
              label
              fab
              small
              :spantext="$t('commons.download_qr_code')"
              @click.stop="openItemUrl(item)"
            />
            <avs-duplicateButton
              @click.stop="$event => duplicateNonConformity(item)"
              :spantext="
                $t('actions.duplicate', {
                  entity: $tc('categories.nonconformity.name', 1).toLowerCase()
                })
              "
            />
            <avs-deleteButton
              :disabled="isNCSigned(item)"
              @click="handleOpenDeleteDialog(item)"
              :spantext="
                $t('actions.delete', {
                  entity: $tc('categories.nonconformity.name', 1).toLowerCase()
                })
              "
            />
          </div>
        </template>
      </v-data-table>
      <avs-deleteDialog
        v-model="deleteDialogIsOpen"
        @cancel="handleCancel"
        @delete="handleDelete"
        resource="Nonconformity"
      /> </template
  ></avs-card>
</template>

<script>
import { truncate } from "lodash"
// import _ from "lodash"
import NonConformityService from "@/services/NonConformityService"
import { toDate } from "@/utils/datetime"
import { Closed, Ongoing, Overdue, Pending } from "@/components/generics/status"
import { dateToString, now, FMTS } from "@/utils/datetime"

export default {
  name: "NonConformitiesTable",
  components: { Closed, Ongoing, Overdue, Pending },
  props: {
    inspectionId: {
      type: Number,
      default: null
    }
  },
  computed: {
    projectId() {
      return this.$store.state.app.project.id
    }
  },
  data() {
    return {
      deleteDialogIsOpen: false,
      selectedItem: null,
      search: "",
      loading: false,
      dataset: [],
      headers: [
        { text: this.$t("commons.nc_number"), value: "nc_number", class: "min-width-150" },
        { text: this.$t("commons.date.report_date"), value: "report_date", class: "min-width-150" },
        { text: this.$t("categories.nonconformity.area"), value: "area" },
        { text: this.$t("categories.nonconformity.event"), value: "event" },
        { text: this.$t("categories.nonconformity.other"), value: "other" },
        { text: this.$tc("commons.picture", 2), value: "pictures", align: "center" },
        { text: this.$t("commons.status.name"), value: "status" },
        { text: this.$t("commons.criticality"), value: "criticality" },
        { text: this.$t("categories.nonconformity.due_to"), value: "due_to" },
        {
          text: this.$t("commons.total_actions"),
          value: "total_actions",
          align: "center"
        },
        { text: this.$t("commons.created_by"), value: "created_by" },
        { text: this.$t("commons.actions"), value: "actions", align: "right", width: 100 }
      ]
    }
  },
  methods: {
    truncate,
    toDate,
    openItemUrl(item) {
      window.open(item.qr_code, "_blank") // Ouvre l'URL dans un nouvel onglet
    },
    getToEditLink(item) {
      return {
        name: "NonConformityCreateUpdate",
        params: { id: item.id }
      }
    },
    getNonConformities() {
      let t = this
      this.loading = true
      if (this.inspectionId) {
        NonConformityService.getByInspection(this.projectId, this.inspectionId)
          .then(r => {
            t.dataset = r.results
          })
          .catch(err => {
            if (err.response && err.response.status === 403) {
              // alert("Access denied: You do not have permission to access this resource.")
              this.$store.commit("app/message", {
                msg: this.$t("messages.error.permission_denied"),
                color: "error"
              })
            }
          })
          .finally(() => {
            t.loading = false
          })
      } else {
        NonConformityService.get(this, this.projectId)
          .then(r => {
            t.dataset = r.results
          })
          .catch(err => {
            if (err.response && err.response.status === 403) {
              // alert("Access denied: You do not have permission to access this resource.")
              this.$store.commit("app/message", {
                msg: this.$t("messages.error.permission_denied"),
                color: "error"
              })
            }
          })
          .finally(() => {
            t.loading = false
          })
      }
    },
    handleDelete(item) {
      NonConformityService.remove(this, this.selectedItem.id).then(r => {
        this.dataset = this.dataset.filter(e => e.id !== this.selectedItem.id)
        this.$store.commit("app/message", {
          msg: this.$t("messages.success.deleted", {
            entity: this.$tc("categories.nonconformity.name", 1)
          }),
          color: "success"
        })
      })
      this.deleteDialogIsOpen = false
    },
    handleOpenDeleteDialog(item) {
      this.deleteDialogIsOpen = true
      this.selectedItem = item
    },
    handleCancel() {
      this.deleteDialogIsOpen = false
    },
    handleStatusComponent(task) {
      return task.status
    },
    toEdit(item) {
      this.$router.push(this.getToEditLink(item))
    },
    isNCSigned(item) {
      return item.state === 4
    },
    async duplicateNonConformity(p) {
      NonConformityService.get(this, this.projectId, p.id)
        .then(async response => {
          // Override some keys
          let date = dateToString(now(), FMTS.FILE)
          response.nc_number = `${response.nc_number}-${date}`
          response.status = "Ongoing"
          response.notify_oncreated = false
          response.lang = this.$i18n.locale

          // Téléchargez les images existantes et créez les nouvelles images
          let picturePromises = response.pictures.map(async picture => {
            let imageBlob = await fetch(picture.file_url).then(res => res.blob())

            // Convertir l'image Blob en base64
            let reader = await new Promise((resolve, reject) => {
              let fileReader = new FileReader()
              fileReader.onloadend = () => resolve(fileReader.result)
              fileReader.onerror = reject
              fileReader.readAsDataURL(imageBlob)
            })

            return {
              file: reader,
              label: "",
              hover: false,
              removed: false,
              timestamp: Date.now()
            }
          })

          // Attendez que toutes les promesses d'images soient résolues
          response.pictures = await Promise.all(picturePromises)

          // On Créé l'inspection
          NonConformityService.create(this, this.projectId, response.inspection.id, response)
            .then(res => {
              this.$store.commit("app/message", {
                msg: this.$t("messages.success.duplicated", {
                  entity: this.$tc("categories.nonconformity.name", 1)
                }),
                color: "success"
              })
              this.getNonConformities()
            })
            .catch(error => {
              this.$emit("error", error)
            })
        })
        .catch(e => {
          if (e.status) {
            this.$store.commit("app/message", {
              msg: this.$t("messages.error.not_allowed_duplicated"),
              color: "error"
            })
          }
        })
    }
  },
  mounted() {
    this.getNonConformities()
    if (!this.inspectionId) {
      this.headers.unshift({ text: "Inspection", value: "inspection.name", class: "min-width-200" })
    }
  }
}
</script>

<style scoped>
.custom-toolbar >>> .v-toolbar__content {
  gap: 1rem;
}
</style>
